import React, { useState, useEffect } from 'react';
import { Nomination } from '../../types/nomination';
import { generateLinkedInPost } from '../../utils/linkedinShare';
import { cleanReason } from './NominationCard';
import './SharePreviewModal.css';

interface SharePreviewModalProps {
  nomination: Nomination;
  onClose: () => void;
  onShare: (imageUrl: string, postText: string) => void;
}

const AWARD_COLORS: Record<string, { bg: string; accent: string; text: string }> = {
  'SPOT Award': { bg: '#fffbeb', accent: '#f59e0b', text: '#92400e' },
  'Quarterly Excellence Award': { bg: '#eff6ff', accent: '#3b82f6', text: '#1e40af' },
};

/** Generate an award card image using Canvas (no server needed) */
function generateAwardImage(nomination: Nomination): Promise<string> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = 630;
    const ctx = canvas.getContext('2d')!;
    const colors = AWARD_COLORS[nomination.awardType] || { bg: '#f0fdf4', accent: '#0d8a6a', text: '#065f46' };

    // Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 1200, 630);

    // Top accent bar
    const grad = ctx.createLinearGradient(0, 0, 1200, 0);
    grad.addColorStop(0, colors.accent);
    grad.addColorStop(1, colors.text);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 1200, 8);

    // Award badge area
    ctx.fillStyle = colors.bg;
    ctx.beginPath();
    ctx.roundRect(60, 40, 1080, 100, 16);
    ctx.fill();

    // Award type text
    ctx.fillStyle = colors.accent;
    ctx.font = 'bold 28px "Segoe UI", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`★ ${nomination.awardType}`, 600, 100);

    // Nominee name
    ctx.fillStyle = '#1e293b';
    ctx.font = 'bold 48px "Segoe UI", Arial, sans-serif';
    ctx.fillText(nomination.nomineeName, 600, 220);

    // "has been recognized for outstanding contribution"
    ctx.fillStyle = '#64748b';
    ctx.font = '24px "Segoe UI", Arial, sans-serif';
    ctx.fillText('has been recognized for outstanding contribution', 600, 270);

    // Divider
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(200, 310);
    ctx.lineTo(1000, 310);
    ctx.stroke();

    // Reason (truncated to fit)
    ctx.fillStyle = '#334155';
    ctx.font = '22px "Segoe UI", Arial, sans-serif';
    ctx.textAlign = 'center';
    const reason = cleanReason(nomination.reason);
    const truncatedReason = reason.length > 200 ? reason.substring(0, 197) + '...' : reason;
    const words = truncatedReason.split(' ');
    let line = '';
    let y = 360;
    for (const word of words) {
      const test = line + word + ' ';
      if (ctx.measureText(test).width > 900 && line) {
        ctx.fillText(line.trim(), 600, y);
        line = word + ' ';
        y += 32;
        if (y > 460) break;
      } else {
        line = test;
      }
    }
    if (line && y <= 460) ctx.fillText(line.trim(), 600, y);

    // Nominated by
    ctx.fillStyle = '#94a3b8';
    ctx.font = '20px "Segoe UI", Arial, sans-serif';
    ctx.fillText(`Nominated by: ${nomination.nominatorName}`, 600, 530);

    // Company branding
    ctx.fillStyle = colors.accent;
    ctx.font = 'bold 18px "Segoe UI", Arial, sans-serif';
    ctx.fillText('Creyente InfoTech', 600, 590);

    resolve(canvas.toDataURL('image/png'));
  });
}

const SharePreviewModal: React.FC<SharePreviewModalProps> = ({ nomination, onClose, onShare }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [postText] = useState(generateLinkedInPost(nomination));

  useEffect(() => {
    generateAwardImage(nomination)
      .then(url => { setImageUrl(url); setLoading(false); })
      .catch(() => { setError('Failed to generate image'); setLoading(false); });
  }, [nomination]);

  const handleShare = () => {
    onShare(imageUrl || '', postText);
  };

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(postText);
      alert('Post text copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownloadImage = () => {
    if (!imageUrl) return;
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `${nomination.nomineeName}-${nomination.awardType}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="share-modal-header">
          <h2>Share on LinkedIn</h2>
          <button className="share-modal-close" onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="share-modal-body">
          <div className="share-preview-section">
            <h3>Preview Image</h3>
            <div className="share-image-container">
              {loading && (
                <div className="share-image-loading">
                  <div className="share-spinner"></div>
                  <p>Generating award image...</p>
                </div>
              )}
              {error && (
                <div className="share-image-error">
                  <p>{error}</p>
                </div>
              )}
              {imageUrl && !loading && (
                <img src={imageUrl} alt="Award preview" className="share-preview-image" />
              )}
            </div>
            {imageUrl && (
              <button className="share-download-btn" onClick={handleDownloadImage}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Download Image
              </button>
            )}
          </div>

          <div className="share-text-section">
            <div className="share-text-header">
              <h3>Post Text</h3>
              <button className="share-copy-btn" onClick={handleCopyText}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
                Copy Text
              </button>
            </div>
            <div className="share-text-preview">{postText}</div>
          </div>
        </div>

        <div className="share-modal-footer">
          <button className="share-cancel-btn" onClick={onClose}>Cancel</button>
          <button className="share-linkedin-btn-primary" onClick={handleShare} disabled={loading}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
            Open LinkedIn & Share
          </button>
        </div>
      </div>
    </div>
  );
};

export default SharePreviewModal;
